import { supabase } from '@/lib/supabase';
import type {
  Project,
  ProductDetails,
  ProductCopy,
  ProductPage,
  AIResponse,
  Section
} from '@/features/projects/types';

export async function createProject(userId: string, name: string, projectType: 'landing_page' | 'widget_form' = 'landing_page'): Promise<Project> {
  const { data, error } = await supabase
    .from('projects')
    .insert({
      user_id: userId,
      name,
      project_type: projectType,
      status: 'draft'
    })
    .select()
    .single();

  if (error) throw error;
  return data as Project;
}

export async function saveProductDetails(projectId: string, details: Partial<ProductDetails>): Promise<ProductDetails> {
  const { data, error } = await supabase
    .from('product_details')
    .upsert({
      project_id: projectId,
      ...details
    }, { onConflict: 'project_id' })
    .select()
    .single();

  if (error) throw error;
  return data as ProductDetails;
}

export async function saveProductCopy(projectId: string, aiResponse: AIResponse): Promise<ProductCopy> {
  const { data, error } = await supabase
    .from('product_copy')
    .upsert({
      project_id: projectId,
      ai_response: aiResponse
    }, { onConflict: 'project_id' })
    .select()
    .single();

  if (error) throw error;
  return data as ProductCopy;
}

export async function saveProductPage(projectId: string, pageData: Partial<ProductPage>): Promise<ProductPage> {
  const { data, error } = await supabase
    .from('product_page')
    .upsert({
      project_id: projectId,
      ...pageData,
      last_saved_at: new Date().toISOString()
    }, { onConflict: 'project_id' })
    .select()
    .single();

  if (error) throw error;
  return data as ProductPage;
}

export async function initializeProductPage(
  projectId: string,
  aiResponse: AIResponse,
  sections: Section[],
  primaryColor: string,
  fontFamily: string
): Promise<ProductPage> {
  const validationForm = aiResponse.validationForm;
  const formFields = validationForm?.fields || [];
  const collectsName = formFields.some((field) => field.key === 'name');
  const emailPrompt = validationForm?.headline || 'Join the waitlist';
  const emailButtonText = validationForm?.ctaLabel || 'Get Early Access';
  const customQuestion = validationForm?.intentQuestion?.label || null;

  const { data, error } = await supabase
    .from('product_page')
    .upsert({
      project_id: projectId,
      page_content: aiResponse,
      visible_sections: sections,
      primary_color: primaryColor,
      font_family: fontFamily,
      font_size: 'medium',
      email_prompt: emailPrompt,
      email_button_text: emailButtonText,
      collect_first_name: validationForm ? collectsName : true,
      collect_last_name: false,
      collect_phone: false,
      custom_question: customQuestion,
      use_brand_name: true,
      show_navbar: true,
      menu_items: []
    }, { onConflict: 'project_id' })
    .select()
    .single();

  if (error) throw error;
  return data as ProductPage;
}
